import {
  handlePromptSubmitError,
  PROMPT_NOT_FOUND_ERROR,
} from '@/app/chat/_common/use-chat-data-query'
import { useCreateWorkflowMutation } from '@/hooks/api/use-create-workflow-mutation'
import { useLoadingDelay } from '@/hooks/use-loading-delay'
import { T } from '@tolgee/react'
import React from 'react'
import { useEffectOnce } from 'react-use'
import { useAuth } from '../_auth/auth-hooks'
import { PromptForm, PromptFormValues } from '../_common/prompt-form'
import { PromptFormFooter } from '../_common/prompt-form-footer'
import { TabsPanelId, useSplitView } from '../_common/split-view/split-view-context'
import { createWorkflowTab } from '../_common/split-view/tabs/factories'
import { isChatContextTab, TabTypeStatic } from '../_common/split-view/tabs/types'

function NewChat() {
  const refTextarea = React.useRef<HTMLTextAreaElement | null>(null)
  const createWorkflow = useCreateWorkflowMutation()
  const { user } = useAuth()
  const { addTab, filterTabs, getTabPositionMetadata } = useSplitView()
  const { disabled, loading, startLoading, stopLoading } = useLoadingDelay()

  const onFormSubmit = React.useCallback(
    async (values: PromptFormValues) => {
      try {
        startLoading()

        const prompt = values.prompt?.trim()
        const files = values.files

        if (!prompt) throw new Error(PROMPT_NOT_FOUND_ERROR)

        const { workflowId, title } = await createWorkflow.mutateAsync({ prompt, files })
        const tabPosition = getTabPositionMetadata({ tabType: TabTypeStatic.NewChat })

        addTab({
          tab: createWorkflowTab({ workflowId, title }),
          index: tabPosition?.index ?? 0,
          panelId: tabPosition?.panelId ?? TabsPanelId.Left,
        })
        filterTabs({
          tabType: TabTypeStatic.NewChat,
        })
      } catch (error: any) {
        console.error(error)
        handlePromptSubmitError(error?.message, 'Failed to create chat')
      } finally {
        stopLoading()
      }
    },
    [addTab, filterTabs, getTabPositionMetadata, startLoading, stopLoading],
  )

  useEffectOnce(() => {
    setTimeout(() => refTextarea.current?.focus())
  })

  return (
    <div className='mx-auto w-full max-w-[752px]'>
      <h1 className='mb-6 text-pretty text-center font-neue-machina text-2xl font-bold'>
        {user?.fullName ? (
          <T keyName={'home.title'} params={{ name: user.fullName?.split(' ')[0] }} />
        ) : (
          'Hi there, how can we assist you?'
        )}
      </h1>
      <PromptForm
        fieldId='home'
        onSubmit={onFormSubmit}
        disabled={disabled}
        loading={loading}
        renderFooter={PromptFormFooter}
        ref={refTextarea}
      />
    </div>
  )
}

export { NewChat }
